import { useEffect, useState, useCallback } from "react";
import { logger } from "@/lib/logger";
import { Link } from "react-router-dom";
import { ArrowLeft, Trophy, Heart, FileSignature, RefreshCw, Share2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import { api } from "@/lib/api";

const WINDOWS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

function PanelHeader({ icon: Icon, title, hint }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-2 mb-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#09090B] text-white flex items-center justify-center">
          <Icon strokeWidth={2.5} className="w-5 h-5" />
        </div>
        <div>
          <div className="ws-label text-[#FF5A00]">{hint}</div>
          <div className="font-display font-black text-2xl tracking-tighter">{title}</div>
        </div>
      </div>
    </div>
  );
}

function EmptyPanel({ label }) {
  return (
    <div className="border-2 border-dashed border-[#09090B] p-10 text-center ws-label text-[#52525B]">
      No {label} in this window yet.
    </div>
  );
}

function StoriesPanel({ stories }) {
  if (stories.length === 0) return <EmptyPanel label="stories" />;
  return (
    <ol className="space-y-3">
      {stories.map((s, i) => (
        <li
          key={s.id}
          data-testid={`leaderboard-story-${i}`}
          className="border-2 border-[#09090B] bg-white grid grid-cols-[40px_80px_1fr_auto] items-center gap-0 ws-hard-shadow-sm overflow-hidden"
        >
          <div className="font-display font-black text-3xl text-center text-[#FF5A00]">
            {String(i + 1).padStart(2, "0")}
          </div>
          <div className="aspect-square bg-[#F4F4F5] border-l-2 border-r-2 border-[#09090B] overflow-hidden">
            <img
              src={s.photo_url}
              alt={s.employee_name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="px-4 py-3 min-w-0">
            <div className="font-display font-bold text-base tracking-tight truncate">{s.employee_name}</div>
            <div className="text-xs text-[#52525B] truncate">“{s.reason}”</div>
          </div>
          <div className="px-4 py-3 border-l-2 border-[#09090B] bg-[#FFCC00] inline-flex items-center gap-2 h-full">
            <Heart strokeWidth={2.5} className="w-4 h-4 fill-[#09090B]" />
            <span className="font-mono font-bold text-[#09090B]">{s.applause}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

function BuzzPanel({ stories }) {
  if (!stories || stories.length === 0) return <EmptyPanel label="buzz" />;
  return (
    <ol className="space-y-3">
      {stories.map((s, i) => (
        <li
          key={s.id}
          data-testid={`leaderboard-buzz-${i}`}
          className="border-2 border-[#09090B] bg-white grid grid-cols-[40px_80px_1fr_auto] items-center gap-0 ws-hard-shadow-sm overflow-hidden"
        >
          <div className="font-display font-black text-3xl text-center text-[#FF5A00]">
            {String(i + 1).padStart(2, "0")}
          </div>
          <div className="aspect-square bg-[#F4F4F5] border-l-2 border-r-2 border-[#09090B] overflow-hidden">
            <img
              src={s.photo_url}
              alt={s.employee_name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="px-4 py-3 min-w-0">
            <div className="font-display font-bold text-base tracking-tight truncate">{s.employee_name}</div>
            <div className="text-xs text-[#52525B] flex items-center gap-2">
              <Heart strokeWidth={2.5} className="w-3 h-3" />{s.applause}
              <span className="text-[#09090B]">·</span>
              <Share2 strokeWidth={2.5} className="w-3 h-3" />{s.share_count ?? 0}
            </div>
          </div>
          <div className="px-4 py-3 border-l-2 border-[#09090B] bg-[#FF5A00] inline-flex items-center gap-2 h-full">
            <span className="ws-label text-white">Buzz</span>
            <span className="font-mono font-bold text-white text-lg">{s.buzz ?? 0}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

function ContributorsPanel({ contributors }) {
  if (contributors.length === 0) return <EmptyPanel label="contributors" />;
  return (
    <ol className="space-y-3">
      {contributors.map((c, i) => (
        <li
          key={c.employee_id}
          data-testid={`leaderboard-contributor-${i}`}
          className="border-2 border-[#09090B] bg-white grid grid-cols-[40px_1fr_auto_auto] items-center gap-0 ws-hard-shadow-sm overflow-hidden"
        >
          <div className="font-display font-black text-3xl text-center text-[#FF5A00]">
            {String(i + 1).padStart(2, "0")}
          </div>
          <div className="px-4 py-3 min-w-0">
            <div className="font-display font-bold text-base tracking-tight truncate">{c.employee_name}</div>
            <div className="text-xs text-[#52525B]">ID · {c.employee_id}</div>
          </div>
          <div className="px-4 py-3 border-l-2 border-[#09090B] inline-flex items-center gap-2 text-sm">
            <FileSignature strokeWidth={2.5} className="w-4 h-4 text-[#FF5A00]" />
            <span className="font-mono font-bold text-[#09090B]">{c.submission_count}</span>
            <span className="ws-label text-[#52525B]">stories</span>
          </div>
          <div className="px-4 py-3 border-l-2 border-[#09090B] inline-flex items-center gap-2 bg-[#FFCC00] h-full">
            <Heart strokeWidth={2.5} className="w-4 h-4 fill-[#09090B]" />
            <span className="font-mono font-bold text-[#09090B]">{c.total_applause}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

function WindowPicker({ days, onChange }) {
  return (
    <div className="inline-flex" data-testid="window-picker">
      {WINDOWS.map((w, idx) => {
        const active = w.days === days;
        const borderLeft = idx === 0 ? "" : "border-l-0";
        const cls = active ? "bg-[#09090B] text-white" : "bg-white text-[#09090B] hover:bg-[#F4F4F5]";
        return (
          <button
            key={w.days}
            type="button"
            onClick={() => onChange(w.days)}
            data-testid={`window-${w.days}d`}
            className={`ws-label px-4 py-2 border-2 border-[#09090B] ${borderLeft} ${cls}`}
          >
            {w.label}
          </button>
        );
      })}
    </div>
  );
}

export default function AdminLeaderboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ top_stories: [], top_buzz: [], top_contributors: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/admin/leaderboard?days=${days}`)
      .then((r) => setData(r.data))
      .catch((err) => logger.warn("[leaderboard] load failed", err))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white min-h-screen text-[#09090B]">
      <SiteHeader />

      <section className="border-b-2 border-[#09090B] bg-[#09090B] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="ws-label text-[#FFCC00]">/ Safety Admin</div>
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3 leading-[0.95] flex items-center gap-4">
              <Trophy strokeWidth={2.5} className="w-10 h-10 text-[#FFCC00]" />
              LEADERBOARD
            </h1>
            <p className="text-[#A1A1AA] mt-2 text-sm max-w-xl">
              Top stories and most-active contributors. Drop into your weekly safety review deck.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <WindowPicker days={days} onChange={setDays} />
            <button
              type="button"
              onClick={load}
              data-testid="leaderboard-refresh-btn"
              className="inline-flex items-center gap-2 bg-white text-[#09090B] ws-label px-4 py-2 border-2 border-white hover:bg-[#FFCC00] transition-colors"
            >
              <RefreshCw strokeWidth={2.5} className="w-4 h-4" /> Refresh
            </button>
            <Link
              to="/admin"
              data-testid="back-to-admin-link"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white ws-label px-4 py-2 hover:bg-[#FF5A00] hover:border-[#FF5A00] transition-colors"
            >
              <ArrowLeft strokeWidth={2.5} className="w-4 h-4" /> Back
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid lg:grid-cols-3 gap-10">
        <div data-testid="stories-panel">
          <PanelHeader icon={Heart} hint="/ 01 — Most loved" title="Top by applause" />
          {loading ? (
            <div className="ws-label text-center py-12">Loading…</div>
          ) : (
            <StoriesPanel stories={data.top_stories} />
          )}
        </div>
        <div data-testid="buzz-panel">
          <PanelHeader icon={Share2} hint="/ 02 — Most buzz" title="Applause + 2× shares" />
          {loading ? (
            <div className="ws-label text-center py-12">Loading…</div>
          ) : (
            <BuzzPanel stories={data.top_buzz} />
          )}
        </div>
        <div data-testid="contributors-panel">
          <PanelHeader icon={FileSignature} hint="/ 03 — Most active" title="Top contributors" />
          {loading ? (
            <div className="ws-label text-center py-12">Loading…</div>
          ) : (
            <ContributorsPanel contributors={data.top_contributors} />
          )}
        </div>
      </section>
    </div>
  );
}
